/**
 * 🎨 Ilustrações SVG premium dos tipos de espaço — Berastays
 *
 * Estilo: linha fina (stroke 1.6–1.8), coloridas, com toques decorativos
 * (palmeiras, plantas, ondas, pássaros) em vez de ícones genéricos.
 * Cada ilustração tem um halo radial suave por trás para um look elevado.
 *
 * Uso: <PropertyIllustration type="Apartamento" className="w-20 h-20" />
 */

import { memo } from "react";

type Props = {
  className?: string;
  /** Cor principal HSL (aceita qualquer formato CSS color). */
  color?: string;
  /** Cor secundária (vegetação/decor). */
  accent?: string;
};

/* ---------- helpers ---------- */
const wrap = (children: React.ReactNode, color: string, accent: string, glow = true) => (
  <svg viewBox="0 0 96 96" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
    {glow && (
      <defs>
        <radialGradient id="halo" cx="50%" cy="58%" r="50%">
          <stop offset="0%" stopColor={color} stopOpacity="0.18" />
          <stop offset="60%" stopColor={color} stopOpacity="0.05" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </radialGradient>
      </defs>
    )}
    {glow && <circle cx="48" cy="56" r="40" fill="url(#halo)" />}
    {children}
  </svg>
);

const baseLine = (color: string) => ({
  stroke: color,
  strokeWidth: 1.7,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  fill: "none",
});

/* ---------- Apartamento (3 torres com janelas) ---------- */
const Apartamento = ({ color = "#3B82F6", accent = "#60A5FA" }: Props) => wrap(
  <g {...baseLine(color)}>
    {/* tower left */}
    <path d="M14 84 V36 L26 30 L26 84 Z" />
    <line x1="18" y1="40" x2="22" y2="40" />
    <line x1="18" y1="48" x2="22" y2="48" />
    <line x1="18" y1="56" x2="22" y2="56" />
    <line x1="18" y1="64" x2="22" y2="64" />
    <line x1="18" y1="72" x2="22" y2="72" />
    {/* tower main */}
    <path d="M30 84 V22 L52 14 L74 22 V84 Z" />
    <line x1="36" y1="30" x2="42" y2="30" />
    <line x1="46" y1="30" x2="52" y2="30" />
    <line x1="56" y1="30" x2="62" y2="30" />
    <line x1="36" y1="40" x2="42" y2="40" />
    <line x1="46" y1="40" x2="52" y2="40" />
    <line x1="56" y1="40" x2="62" y2="40" />
    <line x1="36" y1="50" x2="42" y2="50" />
    <line x1="46" y1="50" x2="52" y2="50" />
    <line x1="56" y1="50" x2="62" y2="50" />
    <line x1="36" y1="60" x2="42" y2="60" />
    <line x1="46" y1="60" x2="52" y2="60" />
    <line x1="56" y1="60" x2="62" y2="60" />
    <rect x="44" y="70" width="10" height="14" rx="1" />
    {/* tower right */}
    <path d="M82 84 V42 L74 38" />
    {/* tree */}
    <path d="M86 84 V74" stroke={accent} />
    <circle cx="86" cy="70" r="5" stroke={accent} fill="none" />
    {/* ground */}
    <line x1="6" y1="84" x2="92" y2="84" />
  </g>,
  color, accent
);

/* ---------- Hotel (edifício com letreiro H) ---------- */
const HotelIll = ({ color = "#7C3AED", accent = "#A78BFA" }: Props) => wrap(
  <g {...baseLine(color)}>
    <path d="M24 84 V20 H72 V84 Z" />
    {/* H sign */}
    <circle cx="48" cy="28" r="5" />
    <path d="M46 25.5 V30.5 M50 25.5 V30.5 M46 28 H50" />
    {/* windows grid */}
    {[36, 48, 60].map((y) => (
      <g key={y}>
        <rect x="30" y={y} width="8" height="6" />
        <rect x="44" y={y} width="8" height="6" />
        <rect x="58" y={y} width="8" height="6" />
      </g>
    ))}
    {/* door */}
    <path d="M42 84 V72 H54 V84" />
    <line x1="48" y1="74" x2="48" y2="82" />
    {/* palm tree */}
    <path d="M16 84 V70" stroke={accent} />
    <path d="M16 70 C 10 66 8 62 10 58 M16 70 C 22 66 24 62 22 58 M16 70 C 14 64 14 60 16 56 M16 70 C 18 64 18 60 16 56" stroke={accent} />
    <line x1="6" y1="84" x2="92" y2="84" />
  </g>,
  color, accent
);

/* ---------- Hospedaria (casa com plantinha) ---------- */
const Hospedaria = ({ color = "#EA580C", accent = "#22C55E" }: Props) => wrap(
  <g {...baseLine(color)}>
    <path d="M22 50 L48 30 L74 50 V84 H22 Z" />
    <path d="M22 50 L20 52 M74 50 L76 52" />
    {/* door */}
    <path d="M42 84 V64 H54 V84" />
    <circle cx="51" cy="74" r="0.9" fill={color} />
    {/* windows */}
    <rect x="28" y="58" width="10" height="8" />
    <line x1="33" y1="58" x2="33" y2="66" />
    <line x1="28" y1="62" x2="38" y2="62" />
    <rect x="58" y="58" width="10" height="8" />
    <line x1="63" y1="58" x2="63" y2="66" />
    <line x1="58" y1="62" x2="68" y2="62" />
    {/* chimney */}
    <path d="M62 38 V30 H66 V42" />
    {/* plant */}
    <path d="M80 84 V78" stroke={accent} />
    <path d="M80 78 C 76 76 74 72 76 70 M80 78 C 84 76 86 72 84 70" stroke={accent} />
    <path d="M76 84 H84" stroke={accent} />
    <line x1="6" y1="84" x2="92" y2="84" />
  </g>,
  color, accent
);

/* ---------- Resort (palmeiras + bangalô + água) ---------- */
const Resort = ({ color = "#10B981", accent = "#F59E0B" }: Props) => wrap(
  <g>
    {/* palm big */}
    <g {...baseLine(color)}>
      <path d="M28 78 C 28 60 26 46 22 36" />
      <path d="M22 36 C 14 30 8 30 6 34 M22 36 C 30 30 36 30 38 34 M22 36 C 18 28 14 22 14 18 M22 36 C 26 28 30 24 32 20" />
      <circle cx="22" cy="36" r="1.5" fill={color} />
    </g>
    {/* bungalow */}
    <g {...baseLine(accent)}>
      <path d="M40 70 L60 56 L80 70" />
      <path d="M44 70 V84 H76 V70" />
      <rect x="56" y="74" width="8" height="10" />
      <line x1="60" y1="74" x2="60" y2="84" />
    </g>
    {/* small palm */}
    <g {...baseLine(color)}>
      <path d="M86 80 C 86 72 88 66 90 62" />
      <path d="M90 62 C 94 60 96 60 96 62 M90 62 C 86 60 84 60 84 62 M90 62 C 92 58 94 56 94 54" />
    </g>
    {/* water waves */}
    <g stroke={accent} strokeWidth="1.5" fill="none" strokeLinecap="round" opacity="0.85">
      <path d="M8 88 Q 14 86 20 88 T 32 88 T 44 88 T 56 88 T 68 88 T 80 88 T 92 88" />
    </g>
  </g>,
  color, accent
);

/* ---------- Quarto T1 (cama + abajur + quadro) ---------- */
const QuartoT1 = ({ color = "#EC4899", accent = "#F472B6" }: Props) => wrap(
  <g {...baseLine(color)}>
    {/* picture */}
    <rect x="36" y="20" width="24" height="16" rx="1.5" />
    <path d="M40 32 L46 26 L50 30 L56 24 L56 32 Z" />
    <circle cx="44" cy="26" r="1.2" />
    {/* bed */}
    <path d="M14 78 V58 C 14 54 18 52 22 52 H74 C 78 52 82 54 82 58 V78" />
    <path d="M22 52 V44 H40 V52 M56 52 V44 H74 V52" />
    {/* pillows */}
    <rect x="24" y="46" width="14" height="6" rx="1" />
    <rect x="58" y="46" width="14" height="6" rx="1" />
    {/* mattress line */}
    <line x1="14" y1="68" x2="82" y2="68" />
    {/* lamp */}
    <path d="M86 78 V62 L90 58 H82 L86 62" stroke={accent} />
    <path d="M82 58 L90 58" stroke={accent} />
    {/* lamp left */}
    <path d="M10 78 V62 L6 58 H14 L10 62" stroke={accent} />
    <line x1="6" y1="84" x2="92" y2="84" />
  </g>,
  color, accent
);

/* ---------- Contentor (com plantas) ---------- */
const Contentor = ({ color = "#0891B2", accent = "#22C55E" }: Props) => wrap(
  <g {...baseLine(color)}>
    <rect x="14" y="42" width="68" height="42" rx="1.5" />
    {/* corrugations */}
    {Array.from({ length: 9 }).map((_, i) => (
      <line key={i} x1={20 + i * 7} y1="42" x2={20 + i * 7} y2="84" opacity="0.5" />
    ))}
    {/* door */}
    <rect x="50" y="50" width="22" height="32" />
    <line x1="61" y1="50" x2="61" y2="82" />
    <circle cx="58" cy="66" r="0.8" fill={color} />
    <circle cx="64" cy="66" r="0.8" fill={color} />
    {/* window */}
    <rect x="22" y="54" width="18" height="12" />
    <line x1="31" y1="54" x2="31" y2="66" />
    <line x1="22" y1="60" x2="40" y2="60" />
    {/* plants beside */}
    <g stroke={accent} fill="none" strokeWidth="1.6" strokeLinecap="round">
      <path d="M86 84 V74" />
      <path d="M86 74 C 82 72 80 68 82 66 M86 74 C 90 72 92 68 90 66" />
      <path d="M8 84 V78" />
      <path d="M8 78 C 5 76 4 72 6 70 M8 78 C 11 76 12 72 10 70" />
    </g>
    <line x1="6" y1="84" x2="92" y2="84" />
  </g>,
  color, accent
);

/* ---------- Barco (iate + mar + gaivota) ---------- */
const Barco = ({ color = "#0EA5E9", accent = "#0369A1" }: Props) => wrap(
  <g>
    {/* seagull */}
    <path d="M62 18 Q 66 14 70 18 M70 18 Q 74 14 78 18" stroke={color} strokeWidth="1.6" fill="none" strokeLinecap="round" />
    {/* yacht body */}
    <g {...baseLine(color)}>
      <path d="M14 64 H82 L74 76 H22 Z" />
      {/* upper deck */}
      <path d="M28 64 V52 H62 L66 64" />
      <rect x="34" y="56" width="6" height="6" />
      <rect x="44" y="56" width="6" height="6" />
      <rect x="54" y="56" width="6" height="6" />
      {/* mast */}
      <line x1="46" y1="52" x2="46" y2="40" />
      <path d="M46 40 H58" />
      {/* hull stripe */}
      <line x1="18" y1="70" x2="78" y2="70" />
    </g>
    {/* waves */}
    <g stroke={accent} strokeWidth="1.6" fill="none" strokeLinecap="round">
      <path d="M6 82 Q 12 80 18 82 T 30 82 T 42 82 T 54 82 T 66 82 T 78 82 T 90 82" />
      <path d="M10 88 Q 16 86 22 88 T 34 88 T 46 88 T 58 88 T 70 88 T 82 88" opacity="0.6" />
    </g>
  </g>,
  color, accent
);

/* ---------- Vivenda (casa moderna + palmeira) ---------- */
const Vivenda = ({ color = "#D97706", accent = "#22C55E" }: Props) => wrap(
  <g {...baseLine(color)}>
    {/* main block */}
    <path d="M24 84 V44 L48 28 L72 44 V84 Z" />
    {/* roof line */}
    <line x1="24" y1="44" x2="72" y2="44" />
    {/* big window */}
    <rect x="32" y="50" width="14" height="18" />
    <line x1="39" y1="50" x2="39" y2="68" />
    <line x1="32" y1="59" x2="46" y2="59" />
    {/* door */}
    <path d="M54 84 V62 H66 V84" />
    <circle cx="62" cy="74" r="0.9" fill={color} />
    {/* small annex */}
    <path d="M72 84 V64 H82 V84" />
    <rect x="74" y="68" width="6" height="6" />
    {/* palm */}
    <g stroke={accent} fill="none" strokeWidth="1.6" strokeLinecap="round">
      <path d="M16 84 V62" />
      <path d="M16 62 C 8 58 4 54 6 50 M16 62 C 24 58 28 54 26 50 M16 62 C 12 56 10 50 12 46 M16 62 C 20 56 22 50 20 46" />
    </g>
    <line x1="6" y1="84" x2="92" y2="84" />
  </g>,
  color, accent
);

/* ---------- Casa de Campo (casa + 2 árvores) ---------- */
const CasaCampo = ({ color = "#059669", accent = "#15803D" }: Props) => wrap(
  <g {...baseLine(color)}>
    {/* trees */}
    <g stroke={accent}>
      <path d="M14 84 V72" />
      <path d="M14 72 C 6 68 4 60 8 56 C 8 50 16 48 18 52 C 22 50 26 56 22 62 C 24 68 18 72 14 72 Z" />
      <path d="M84 84 V70" />
      <path d="M84 70 C 76 66 76 56 82 54 C 82 48 90 50 90 56 C 94 60 90 68 84 70 Z" />
    </g>
    {/* house */}
    <path d="M28 84 V52 L48 38 L68 52 V84 Z" />
    <path d="M28 52 L24 56 M68 52 L72 56" />
    {/* roof texture */}
    <line x1="34" y1="50" x2="48" y2="40" />
    <line x1="42" y1="50" x2="48" y2="44" />
    <line x1="62" y1="50" x2="48" y2="40" />
    <line x1="54" y1="50" x2="48" y2="44" />
    {/* door */}
    <path d="M44 84 V66 H52 V84" />
    {/* windows */}
    <rect x="32" y="60" width="8" height="8" />
    <line x1="36" y1="60" x2="36" y2="68" />
    <line x1="32" y1="64" x2="40" y2="64" />
    <rect x="56" y="60" width="8" height="8" />
    <line x1="60" y1="60" x2="60" y2="68" />
    <line x1="56" y1="64" x2="64" y2="64" />
    {/* chimney */}
    <path d="M58 44 V36 H62 V47" />
    <line x1="6" y1="84" x2="92" y2="84" />
  </g>,
  color, accent
);

/* ---------- Casa de Praia (casa + palmeira + ondas) ---------- */
const CasaPraia = ({ color = "#06B6D4", accent = "#F59E0B" }: Props) => wrap(
  <g>
    <g {...baseLine(color)}>
      {/* house on stilts */}
      <path d="M30 76 V52 L52 38 L74 52 V76 Z" />
      <path d="M30 52 L26 56 M74 52 L78 56" />
      <line x1="36" y1="76" x2="36" y2="84" />
      <line x1="68" y1="76" x2="68" y2="84" />
      {/* door */}
      <path d="M48 76 V62 H58 V76" />
      {/* window */}
      <rect x="36" y="58" width="8" height="8" />
      <line x1="40" y1="58" x2="40" y2="66" />
      <line x1="36" y1="62" x2="44" y2="62" />
      <rect x="60" y="58" width="8" height="8" />
      <line x1="64" y1="58" x2="64" y2="66" />
    </g>
    {/* palm */}
    <g stroke={accent} fill="none" strokeWidth="1.6" strokeLinecap="round">
      <path d="M16 80 V60" />
      <path d="M16 60 C 8 56 4 52 6 48 M16 60 C 24 56 28 52 26 48 M16 60 C 12 54 10 48 12 44 M16 60 C 20 54 22 48 20 44" />
    </g>
    {/* waves */}
    <g stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round" opacity="0.85">
      <path d="M6 88 Q 14 86 22 88 T 38 88 T 54 88 T 70 88 T 86 88" />
    </g>
  </g>,
  color, accent
);

/* ---------- Guest House (edifício compacto + flores) ---------- */
const GuestHouse = ({ color = "#F59E0B", accent = "#EC4899" }: Props) => wrap(
  <g {...baseLine(color)}>
    <path d="M22 84 V32 H74 V84 Z" />
    <path d="M22 32 L48 18 L74 32" />
    {/* awning */}
    <path d="M22 44 H74" />
    {/* windows with shutters */}
    <rect x="30" y="50" width="10" height="10" />
    <line x1="35" y1="50" x2="35" y2="60" />
    <rect x="56" y="50" width="10" height="10" />
    <line x1="61" y1="50" x2="61" y2="60" />
    <rect x="30" y="64" width="10" height="10" />
    <line x1="35" y1="64" x2="35" y2="74" />
    <rect x="56" y="64" width="10" height="10" />
    <line x1="61" y1="64" x2="61" y2="74" />
    {/* door */}
    <path d="M44 84 V66 H52 V84" />
    <circle cx="50" cy="76" r="0.8" fill={color} />
    {/* flowers */}
    <g stroke={accent} fill="none" strokeWidth="1.4" strokeLinecap="round">
      <circle cx="14" cy="78" r="2.2" />
      <circle cx="84" cy="78" r="2.2" />
      <path d="M14 80 V84 M84 80 V84" />
    </g>
    <line x1="6" y1="84" x2="92" y2="84" />
  </g>,
  color, accent
);

/* ---------- Suite Privada (cama king + criados-mudos + abajures) ---------- */
const SuitePrivada = ({ color = "#C026D3", accent = "#F472B6" }: Props) => wrap(
  <g {...baseLine(color)}>
    {/* art */}
    <rect x="36" y="18" width="24" height="14" rx="1.5" />
    <path d="M40 30 L46 24 L50 28 L56 22 L56 30 Z" />
    {/* headboard */}
    <path d="M22 56 V44 H74 V56" />
    {/* mattress */}
    <path d="M16 78 V58 C 16 56 18 54 20 54 H76 C 78 54 80 56 80 58 V78" />
    {/* pillows */}
    <rect x="26" y="48" width="18" height="6" rx="1" />
    <rect x="52" y="48" width="18" height="6" rx="1" />
    {/* duvet */}
    <line x1="16" y1="68" x2="80" y2="68" />
    {/* nightstands */}
    <rect x="6" y="68" width="10" height="14" />
    <rect x="80" y="68" width="10" height="14" />
    {/* lamps */}
    <g stroke={accent}>
      <path d="M11 68 V60 L8 56 H14 L11 60" />
      <path d="M85 68 V60 L82 56 H88 L85 60" />
    </g>
    <line x1="2" y1="84" x2="94" y2="84" />
  </g>,
  color, accent
);

/* ---------- Map ---------- */
const ILLUSTRATIONS: Record<string, (p: Props) => React.ReactElement> = {
  "Apartamento": Apartamento,
  "Hotel": HotelIll,
  "Hospedaria": Hospedaria,
  "Resort": Resort,
  "Quarto T1": QuartoT1,
  "Contentor": Contentor,
  "Barco": Barco,
  "Vivenda": Vivenda,
  "Casa de Campo": CasaCampo,
  "Casa de Praia": CasaPraia,
  "Guest House": GuestHouse,
  "Suite Privada": SuitePrivada,
};

type IllProps = {
  type: string;
  className?: string;
  color?: string;
  accent?: string;
};

const PropertyIllustration = memo(({ type, className, color, accent }: IllProps) => {
  const Comp = ILLUSTRATIONS[type];
  if (!Comp) return null;
  return (
    <div className={className}>
      <Comp color={color} accent={accent} />
    </div>
  );
});

PropertyIllustration.displayName = "PropertyIllustration";

export default PropertyIllustration;
